import { FC, useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { CommerceNoticeDoc } from '@gooduncles/gu-app-schema';

import TiptapImageExtension, { resolveContentImageUrls } from '../CommerceNoticeEditor/TiptapImageExtension';

import styles from './CommerceNoticeDetail.module.scss';

interface CommerceNoticeDetailProps {
  notice: CommerceNoticeDoc;
}

const CommerceNoticeDetail: FC<CommerceNoticeDetailProps> = ({ notice }) => {
  const images = notice.images ?? {};

  const resolvedContent = useMemo(() => resolveContentImageUrls(notice.content, images), [notice.content, images]);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        Underline,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        }),
        TiptapImageExtension,
      ],
      content: resolvedContent ?? undefined,
      editable: false,
    },
    [notice._id, resolvedContent],
  );

  useEffect(() => {
    if (!editor) return;
    const element = editor.view.dom;
    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;
      e.preventDefault();
      const href = anchor.getAttribute('href');
      if (!href) return;
      const url = /^https?:\/\//.test(href) ? href : `https://${href}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    };
    element.addEventListener('click', handleClick);
    return () => element.removeEventListener('click', handleClick);
  }, [editor]);

  return (
    <div className={styles.detailContent}>
      <EditorContent editor={editor} />
    </div>
  );
};

export default CommerceNoticeDetail;
